import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

const ago = (iso) => {
  if (!iso) return "never";
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  if (m < 48 * 60) return `${Math.round(m / 60)} h ago`;
  return `${Math.round(m / 1440)} days ago`;
};

// Login footer pill (Electron only): Server reachable? license last verified? offline grace left?
export default function ServerHealthBadge({ onHealth }) {
  const api = window.electronAPI;
  const [h, setH] = useState(null);
  const [busy, setBusy] = useState(false);

  const check = useCallback(async () => {
    if (!api?.serverHealth) return;
    setBusy(true);
    let res;
    try { res = await api.serverHealth(); } catch (e) { res = { reachable: false, status: "error", error: e.message }; }
    setH(res);
    if (onHealth) onHealth(res);
    setBusy(false);
  }, [api, onHealth]);

  useEffect(() => {
    check();
    const t = setInterval(check, 60000);
    return () => clearInterval(t);
  }, [check]);

  if (!api?.serverHealth) return null;
  const ok = !!h?.reachable;
  const denied = h && h.reachable && h.allowed === false;
  const color = !h ? "text-zinc-400 border-zinc-200" : denied ? "text-red-700 border-red-200 bg-red-50" : ok ? "text-green-700 border-green-200 bg-green-50" : "text-amber-700 border-amber-200 bg-amber-50";
  const dot = !h ? "bg-zinc-300" : denied ? "bg-red-500" : ok ? "bg-green-500" : "bg-amber-500";
  const label = !h ? "Checking Server…" : denied ? (h.status === "trial_expired" ? "Trial ended" : `License ${h.status.replace(/_/g, " ")}`) : ok ? "Server online" : "Server offline";
  const detail = !h || ok ? "" : h.grace_total === 0 ? `no offline grace — last verified ${ago(h.verified_at)}` : h.grace_days_left === null ? "no license cache" : `grace: ${h.grace_days_left} of ${h.grace_total} days left · last verified ${ago(h.verified_at)}`;

  return (
    <button
      type="button"
      onClick={check}
      disabled={busy}
      title={`${h?.central_host || ""}${h?.error ? " — " + h.error : ""}\nClick to re-check now`}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[12px] leading-tight whitespace-nowrap transition-colors hover:brightness-95 ${color}`}
      data-testid="server-health-badge"
    >
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <span className={`w-2 h-2 rounded-full ${dot}`} data-testid="server-health-dot" />}
      <span className="font-bold" data-testid="server-health-label">{label}</span>
      {detail && <span className="opacity-80" data-testid="server-health-detail">· {detail}</span>}
    </button>
  );
}
